/**
 * Agenda de citas/sesiones (coach) — espejo del router backend
 * `aetherlite_appointments`. El coach fija la hora: crea citas sueltas o
 * recurrentes (Lun/Mié/Vie 4pm × N semanas) y puede reagendar/cancelar/borrar
 * cada una. Misma API que usan las apps móviles (paridad web ↔ apps).
 */
import { apiFetch } from "./client";

export type AppointmentStatus = "scheduled" | "cancelled" | "completed" | "no_show";

export interface Appointment {
  id: number;
  client_id: number;
  starts_at: string; // ISO datetime
  duration_min: number;
  modality: string | null; // virtual | presencial | hibrido
  title: string | null;
  notes: string | null;
  status: AppointmentStatus;
}

export interface AppointmentCreate {
  starts_at: string;
  duration_min?: number;
  modality?: string | null;
  title?: string | null;
  notes?: string | null;
}

export interface RecurringCreate {
  weekdays: number[]; // 0=Lun … 6=Dom
  time: string; // "HH:MM"
  start_date: string; // YYYY-MM-DD
  weeks: number;
  duration_min?: number;
  modality?: string | null;
  title?: string | null;
}

/** Cita con nombre del cliente — para la agenda global del coach. */
export interface CoachAppointment {
  id: number;
  client_id: number;
  client_name: string;
  starts_at: string;
  duration_min: number;
  modality: string | null;
  title: string | null;
  status: AppointmentStatus;
}

/** Todas las citas de los clientes del coach (timeline del día). */
export async function listAllAppointments(): Promise<CoachAppointment[]> {
  return apiFetch<CoachAppointment[]>("/aetherlite/appointments");
}

export async function listClientAppointments(clientId: number): Promise<Appointment[]> {
  return apiFetch<Appointment[]>(`/aetherlite/clients/${clientId}/appointments`);
}

export async function createAppointment(
  clientId: number,
  input: AppointmentCreate,
): Promise<Appointment> {
  return apiFetch<Appointment>(`/aetherlite/clients/${clientId}/appointments`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function createRecurringAppointments(
  clientId: number,
  input: RecurringCreate,
): Promise<Appointment[]> {
  return apiFetch<Appointment[]>(`/aetherlite/clients/${clientId}/appointments/recurring`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateAppointment(
  id: number,
  patch: Partial<Pick<Appointment, "starts_at" | "duration_min" | "modality" | "title" | "notes" | "status">>,
): Promise<Appointment> {
  return apiFetch<Appointment>(`/aetherlite/appointments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function deleteAppointment(id: number): Promise<void> {
  await apiFetch(`/aetherlite/appointments/${id}`, { method: "DELETE" });
}

// ---------- Helpers de fecha (hora local Colombia, UTC-5 sin DST) ----------

const COL_OFFSET = "-05:00";

/** Combina YYYY-MM-DD + HH:MM en un ISO con offset de Colombia (-05:00), para
 *  que el instante absoluto que se guarda sea la hora local que eligió el coach
 *  (sin esto el naive se interpretaba como UTC y se corría 5h). */
export function toColombiaIso(dateYmd: string, timeHm: string): string {
  return `${dateYmd}T${timeHm}:00${COL_OFFSET}`;
}

/** Etiqueta de día relativa: "Hoy", "Mañana" o "lun 26 may". */
export function dayLabel(d: Date): string {
  const today = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = Math.round((startOf(d).getTime() - startOf(today).getTime()) / 86400000);
  if (diff === 0) return "Hoy";
  if (diff === 1) return "Mañana";
  if (diff === -1) return "Ayer";
  return d.toLocaleDateString("es", { weekday: "short", day: "numeric", month: "short" });
}

/** Hora local legible: "4:30 PM". */
export function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString("es", { hour: "numeric", minute: "2-digit", hour12: true });
}

/** Clave YYYY-MM-DD local para agrupar por día. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
